import { type JSX, splitProps } from 'solid-js'

export type IconButtonSize = 'sm' | 'control' | 'md' | 'lg'
export type IconButtonTone = 'neutral' | 'danger' | 'inherit' | 'primary' | 'soft'
export type IconButtonShape = 'square' | 'round'

export interface IconButtonProps extends JSX.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  size?: IconButtonSize
  tone?: IconButtonTone
  shape?: IconButtonShape
}

const sizeClasses: Record<IconButtonSize, string> = {
  sm: 'h-9 w-9',
  control: 'h-9 w-9',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
}

const squareRadius: Record<IconButtonSize, string> = {
  sm: 'rounded-[var(--radius-12)]',
  control: 'rounded-[var(--radius-12)]',
  md: 'rounded-[var(--radius-12)]',
  lg: 'rounded-[var(--radius-16)]',
}

const toneClasses: Record<IconButtonTone, string> = {
  neutral: `
    bg-[var(--color-surface)] disabled:opacity-(--opacity-fill-disabled)
    border-[var(--color-field-border)]
    text-[color:var(--color-text-muted)]
    hover:not-disabled:bg-[var(--color-neutral-100)]
    hover:not-disabled:border-[var(--color-neutral-400)]
    hover:not-disabled:text-[color:var(--color-text)]
    focus-visible:outline-[var(--color-primary-500)]
  `,
  danger: `
    bg-transparent disabled:opacity-(--opacity-fill-disabled)
    border-[var(--color-field-border)]
    text-[color:var(--color-text-muted)]
    hover:not-disabled:border-[var(--color-error)]
    hover:not-disabled:bg-[var(--color-error-50)]
    hover:not-disabled:text-[color:var(--color-error-600)]
    focus-visible:outline-[var(--color-error-600)]
  `,
  inherit: `
    bg-transparent disabled:opacity-(--opacity-fill-disabled)
    border-transparent
    text-current
    hover:not-disabled:bg-[color-mix(in_srgb,currentColor_12%,transparent)]
    focus-visible:outline-current
  `,
  soft: `
    border-transparent
    bg-[color-mix(in_srgb,var(--color-text)_10%,transparent)]
    text-[color:var(--color-text-muted)]
    hover:not-disabled:bg-[color-mix(in_srgb,var(--color-text)_14%,transparent)]
    hover:not-disabled:text-[color:var(--color-text)]
    focus-visible:outline-[var(--color-primary-500)]
  `,
  primary: `
    border-transparent disabled:opacity-(--opacity-fill-disabled)
    bg-[var(--color-primary-500)]
    text-[color:var(--color-on-primary)]
    hover:not-disabled:bg-[var(--color-primary-600)]
    focus-visible:outline-[var(--color-primary-500)] focus-visible:outline-offset-2
  `,
}

const PRESSED_CLASSES = `
    aria-pressed:text-[color:var(--color-primary-500)]
    aria-pressed:hover:not-disabled:text-[color:var(--color-primary-600)]
  `

const PRESSABLE_TONES: ReadonlySet<IconButtonTone> = new Set(['neutral', 'inherit', 'soft'])

export interface IconButtonClassOptions {
  size?: IconButtonSize | undefined
  tone?: IconButtonTone | undefined
  shape?: IconButtonShape | undefined
  class?: string | undefined
}

export function iconButtonClass(options: IconButtonClassOptions = {}): string {
  const size = options.size ?? 'md'
  const tone = options.tone ?? 'neutral'
  return `
        ds-icon-button inline-flex flex-shrink-0 items-center justify-center cursor-pointer
        border
        transition-all duration-[var(--duration-normal)] ease-[var(--ease-component)]
        motion-safe:active:not-disabled:scale-95
        focus-visible:outline-2 focus-visible:-outline-offset-2
        disabled:cursor-not-allowed
        ${toneClasses[tone]}
        ${PRESSABLE_TONES.has(tone) ? PRESSED_CLASSES : ''}
        ${sizeClasses[size]}
        ${options.shape === 'round' ? 'rounded-full' : squareRadius[size]}
        ${options.class ?? ''}
      `.trim()
}

export function IconButton(props: IconButtonProps) {
  const [local, rest] = splitProps(props, ['label', 'size', 'tone', 'shape', 'class', 'children'])

  return (
    <button
      type="button"
      title={local.label}
      {...rest}
      aria-label={local.label}
      class={iconButtonClass({
        size: local.size,
        tone: local.tone,
        shape: local.shape,
        class: local.class,
      })}
    >
      {local.children}
    </button>
  )
}
